import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiArrowLeft } = FiIcons;

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
            <span>Back to Status Checker</span>
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiFileText} className="h-8 w-8 text-blue-500" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Terms of Service
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md space-y-6"
        >
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Agreement to Terms
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              By accessing and using Web Down Status, you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do 
              not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Service Description
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Web Down Status is a free website monitoring and status checking tool that provides:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Website availability checking</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Performance analysis and metrics</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Security header analysis</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">DNS resolution information</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Acceptable Use
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              You agree to use this service only for lawful purposes and in a way that does not infringe 
              the rights of, restrict, or inhibit anyone else's use of the service. Prohibited behavior includes:
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Using the service to check websites you do not own or have permission to monitor
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Attempting to overwhelm the service with excessive requests (rate limiting applies)
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Using the service for any illegal or unauthorized purpose
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Attempting to reverse engineer, hack, or compromise the service
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Rate Limiting
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              To ensure fair usage and service availability for all users, we implement rate limiting:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Maximum of 30 requests per minute per IP address</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Temporary blocking may occur for excessive usage</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Service Availability
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              While we strive to maintain high availability, we do not guarantee that the service will be 
              available 100% of the time. The service is provided "as is" and may be subject to:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Scheduled maintenance and updates</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Unexpected downtime or technical issues</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Third-party service dependencies</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Accuracy of Results
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              While we strive to provide accurate status information, the results should be used as a 
              general indication only. Factors that may affect accuracy include:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Network conditions and geographical location</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">CORS policies and browser limitations</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Temporary website issues or maintenance</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Limitation of Liability
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              In no event shall Web Down Status or its operators be liable for any direct, indirect, 
              incidental, special, consequential, or punitive damages, including without limitation, 
              loss of profits, data, use, goodwill, or other intangible losses, resulting from your 
              use of the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Intellectual Property
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Web Down Status is open source software. The source code is available under the MIT License. 
              You are free to use, modify, and distribute the code according to the license terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Modifications to Terms
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              We reserve the right to modify these terms at any time. Changes will be effective immediately 
              upon posting to this page. Your continued use of the service after any changes constitutes 
              acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Governing Law
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              These terms shall be interpreted and governed by the laws applicable in the jurisdiction 
              where the service is operated, without regard to conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Contact Information
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
              <br />
              <a href="mailto:legal@webdownstatus.com" className="text-primary-600 dark:text-primary-400 hover:underline">
                legal@webdownstatus.com
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;